/**
 * Utilisateurs GA4 dans le temps, une courbe par canal ou par pays.
 *
 * Les neuf plus gros de la période ont leur courbe, le reste est regroupé en
 * « Autres ». Même unité partout : un seul axe. Par semaine au-delà de deux
 * mois, par tranche de 30 jours au-delà d'un an.
 *
 * ⚠️ Une tranche de plusieurs jours ADDITIONNE les utilisateurs de ses jours :
 * un visiteur revenu deux fois dans la semaine y compte deux fois. Le libellé
 * le dit (« visites d'utilisateurs »).
 */

import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatCompact, formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, CATEGORICAL_COLORS, COLORS, LINE_WIDTH, OTHER_COLOR } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';

const TOP = 9;
const OTHERS = 'Autres';

type Bucket = { date: string } & Record<string, number | string>;

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

export function Ga4SeriesChart({
  title,
  noun,
  dates,
  daily,
  labelOf = (value) => value,
}: {
  title: string;
  /** « canaux », « pays » — pour le sous-titre. */
  noun: string;
  /** Tous les jours de la période, dans l'ordre. */
  dates: readonly string[];
  daily: ReadonlyArray<{ date: string; value: string; users: number }>;
  labelOf?: (value: string) => string;
}) {
  const bucketSize = bucketSizeFor(dates.length);

  const { series, buckets } = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of daily) totals.set(labelOf(row.value), (totals.get(labelOf(row.value)) ?? 0) + row.users);
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([channel]) => channel);
    const top = ranked.slice(0, TOP);
    const keys = ranked.length > TOP ? [...top, OTHERS] : top;
    const keyOf = (channel: string) => (top.includes(channel) ? channel : OTHERS);

    const byDay = new Map<string, Map<string, number>>();
    for (const row of daily) {
      const key = keyOf(labelOf(row.value));
      const day = byDay.get(row.date) ?? new Map<string, number>();
      day.set(key, (day.get(key) ?? 0) + row.users);
      byDay.set(row.date, day);
    }

    const out: Bucket[] = [];
    for (let start = 0; start < dates.length; start += bucketSize) {
      const slice = dates.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const bucket: Bucket = { date: first };
      for (const key of keys) bucket[key] = slice.reduce((sum, date) => sum + (byDay.get(date)?.get(key) ?? 0), 0);
      out.push(bucket);
    }
    return {
      series: keys.map((key, index) => ({ key, color: key === OTHERS ? OTHER_COLOR : CATEGORICAL_COLORS[index] ?? OTHER_COLOR })),
      buckets: out,
    };
  }, [daily, dates, bucketSize, labelOf]);

  const columns: ReadonlyArray<DataColumn<Bucket>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'Tranche', render: (row) => bucketLabel(row.date, bucketSize) },
    ...series.map((entry) => ({
      key: entry.key,
      header: entry.key,
      numeric: true,
      render: (row: Bucket) => formatInteger(Number(row[entry.key] ?? 0)),
    })),
  ];
  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';

  return (
    <ChartCard
      title={title}
      subtitle={`Utilisateurs ${granularity}${bucketSize > 1 ? ' (visites d\'utilisateurs : un visiteur revenu plusieurs jours compte plusieurs fois)' : ''} — les ${TOP} premiers ${noun}, le reste en « ${OTHERS} »`}
      className="chart-grid__wide"
      rows={series.length > 0 ? buckets : []}
      columns={columns}
      emptyMessage="Aucune donnée GA4 sur cette période."
      chartHeight={300}
    >
      <ChartLegend entries={series.map((entry) => ({ label: entry.key, color: entry.color }))} />
      <ResponsiveContainer width="100%" height="86%">
        <LineChart data={buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
          <YAxis tickFormatter={formatCompact} tick={axisTick} axisLine={false} tickLine={false} width={48} />
          <Tooltip
            labelFormatter={(date) => bucketLabel(String(date), bucketSize)}
            formatter={(value) => formatInteger(Number(value))}
            contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.grid}` }}
          />
          {series.map((entry) => (
            <Line
              key={entry.key}
              type="monotone"
              dataKey={entry.key}
              name={entry.key}
              stroke={entry.color}
              strokeWidth={LINE_WIDTH}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
